// product-grid.js
class ProductGrid extends HTMLElement {
  constructor() {
    super();
    this.currentPage = 1;
    this.isLoading = false;
    this.hasMore = true;
    this.maxProducts = parseInt(this.dataset.maxProducts);
    this.collection = this.dataset.collection;
    this.aiGenId = this.dataset.aiGenId;
    this.loadedCount = 0;
    this.buyButtonsCache = new Map();
  }

connectedCallback() {
  this.gridContainer = this.querySelector('[data-grid-container]');
  this.loadingIndicator = this.querySelector('[data-loading]');
  this.drawer = document.querySelector(`[data-drawer]`);
  this.drawerBackdrop = document.querySelector(`[data-drawer-backdrop]`);
  this.drawerClose = document.querySelector(`[data-drawer-close]`);
  this.drawerAddToCartContainer = document.querySelector(`[data-drawer-add-to-cart]`);
  this.currentProductData = null;

  // Count liquid-rendered products (max 50)
  this.loadedCount = this.gridContainer.querySelectorAll(`.ai-product-card-${this.aiGenId}`).length;

  this.setupQuickAdd();
  this.setupDrawer();
  this.makeCardsClickable();

  // Pre-load buy buttons
  this.preloadBuyButtons();

  // Load ALL remaining products at once
  this.loadAllProducts();
}


async loadAllProducts() {
  try {
    this.loadingIndicator.style.display = 'block';

    const url = `/collections/${this.collection}/products.json?limit=250`;
    const response = await fetch(url);
    const data = await response.json();

    const products = data.products || [];

    for (const product of products) {
      // Skip products already rendered by Liquid
      const alreadyExists = this.gridContainer.querySelector(
        `[data-product-id="${product.id}"]`
      );
      if (alreadyExists) continue;

      await this.addProductToGrid(product.handle);
    }

  } catch (err) {
    console.error("Failed to load full collection:", err);
  }

  this.loadingIndicator.style.display = 'none';
}

  makeCardsClickable() {
    const cards = this.querySelectorAll(`.ai-product-card-${this.aiGenId}`);
    
    cards.forEach(card => {
      const linkEl = card.querySelector(`.ai-product-card__title-${this.aiGenId} a`);
      if (!linkEl) return;
      const productUrl = linkEl.getAttribute('href');

      card.addEventListener('click', (e) => {
        // Ignore interactive elements
        if (
          e.target.closest('[data-quick-add]') ||
          e.target.closest(`.ai-product-card__overlay-${this.aiGenId}`) ||
          e.target.closest('button')
        ) return;

        window.location.href = productUrl;
      });
    });
  }

  setupQuickAdd() {
    this.addEventListener('click', (e) => {
      const quickAddBtn = e.target.closest('[data-quick-add]');
      if (!quickAddBtn) return;

      e.preventDefault();
      const productHandle = quickAddBtn.dataset.productHandle;
      this.loadProductData(productHandle, quickAddBtn);
    });
  }

  async loadProductData(handle, button) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      const product = await response.json();
      this.currentProductData = product;

      const sizeOption = product.options.find(opt => 
        opt.name.toLowerCase().includes('size')
      );

      if (!sizeOption) {
        this.addToCart(product.variants[0].id);
        return;
      }

      const isMobile = window.innerWidth < 750;
      if (isMobile) {
        this.showDrawer(product, sizeOption);
      } else {
        this.showOverlay(button, product, sizeOption);
      }
    } catch (error) {
      console.error('Error loading product:', error);
    }
  }

  showOverlay(button, product, sizeOption) {
    const card = button.closest(`.ai-product-card-${this.aiGenId}`);
    const overlay = card.querySelector('[data-overlay]');
    const sizesContainer = overlay.querySelector('[data-sizes]');
    const addButton = overlay.querySelector('[data-add-button]');

    sizesContainer.innerHTML = '';
    sizeOption.values.forEach(size => {
      const pill = document.createElement('div');
      pill.className = `ai-size-pill-${this.aiGenId}`;
      pill.textContent = size;
      pill.dataset.size = size;
      pill.addEventListener('click', () => {
        overlay.querySelectorAll(`.ai-size-pill-${this.aiGenId}`).forEach(p => 
          p.classList.remove('selected')
        );
        pill.classList.add('selected');
        addButton.classList.add('enabled');
        addButton.textContent = 'Add to Cart';
        addButton.dataset.selectedSize = size;
      });
      sizesContainer.appendChild(pill);
    });

    addButton.classList.remove('enabled');
    addButton.textContent = 'Select Size';
    addButton.onclick = () => {
      if (!addButton.classList.contains('enabled')) return;
      const selectedSize = addButton.dataset.selectedSize;
      const variant = product.variants.find(v => {
        const sizeOptionIndex = product.options.findIndex(opt => 
          opt.name.toLowerCase().includes('size')
        );
        return v.options[sizeOptionIndex] === selectedSize;
      });
      if (variant) {
        this.addToCart(variant.id, card);
        overlay.classList.remove('active');
      }
    };

    overlay.classList.add('active');
    
    const closeBtn = overlay.querySelector(`.ai-product-overlay__close-${this.aiGenId}`);
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        overlay.classList.remove('active');
      });
    }

    const closeOverlay = (e) => {
      if (!overlay.contains(e.target) && !button.contains(e.target)) {
        overlay.classList.remove('active');
        document.removeEventListener('click', closeOverlay);
      }
    };

    document.addEventListener('click', closeOverlay, true);
  }

  showDrawer(product, sizeOption) {
    const drawerProduct = this.drawer.querySelector('[data-drawer-product]');
    const drawerSizes = this.drawer.querySelector('[data-drawer-sizes]');
    const drawerButton = this.drawer.querySelector('[data-drawer-button]');

    // Show drawer immediately
    this.drawer.classList.add('active');
    this.drawerBackdrop.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Populate drawer content
    drawerProduct.innerHTML = `
      <img src="${product.featured_image}" alt="${product.title}" class="ai-drawer__image-${this.aiGenId}">
      <div class="ai-drawer__info-${this.aiGenId}">
        <h3 class="ai-drawer__title-${this.aiGenId}">${product.title}</h3>
        <div class="ai-drawer__price-${this.aiGenId}">${this.formatMoney(product.price)}</div>
      </div>
    `;

    drawerSizes.innerHTML = '';
    sizeOption.values.forEach(size => {
      const pill = document.createElement('div');
      pill.className = `ai-size-pill-${this.aiGenId}`;
      pill.textContent = size;
      pill.dataset.size = size;
      pill.addEventListener('click', () => {
        drawerSizes.querySelectorAll(`.ai-size-pill-${this.aiGenId}`).forEach(p => 
          p.classList.remove('selected')
        );
        pill.classList.add('selected');
        drawerButton.classList.add('enabled');
        drawerButton.textContent = 'Add to Cart';
        drawerButton.dataset.selectedSize = size;
      });
      drawerSizes.appendChild(pill);
    });

    drawerButton.classList.remove('enabled');
    drawerButton.textContent = 'Select Size';
    drawerButton.onclick = () => {
      if (!drawerButton.classList.contains('enabled')) return;
      const selectedSize = drawerButton.dataset.selectedSize;
      const variant = product.variants.find(v => {
        const sizeOptionIndex = product.options.findIndex(opt =>
          opt.name.toLowerCase().includes('size')
        );
        return v.options[sizeOptionIndex] === selectedSize;
      });

      if (variant) {
        this.addToCartFromDrawer(variant.id);
        this.closeDrawer();
      }
    };

    // Load buy buttons in background (non-blocking)
    this.loadBuyButtonsForDrawer(product).catch(console.error);
  }

  async loadBuyButtonsForDrawer(product) {
    // Check cache first
    if (this.buyButtonsCache.has(product.handle)) {
      return this.buyButtonsCache.get(product.handle);
    }

    try {
      // Fetch buy buttons HTML
      const response = await fetch(`/products/${product.handle}`);
      const html = await response.text();
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find the buy buttons in the product page
      const buyButtons = doc.querySelector('product-form, [data-product-form], form[action="/cart/add"]');
      
      if (buyButtons) {
        this.buyButtonsCache.set(product.handle, buyButtons.outerHTML);
        return buyButtons.outerHTML;
      }
    } catch (error) {
      console.error('Error loading buy buttons:', error);
    }
    
    return null;
  }

  addToCartFromDrawer(variantId) {
    const form = this.drawerAddToCartContainer.querySelector('form');
    if (!form) {
      console.warn('No form found in drawer');
      return;
    }

    const variantInput = form.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variantId;
    }

    // Use the theme's native form submission
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }

  setupDrawer() {
    this.drawerClose.addEventListener('click', () => this.closeDrawer());
    this.drawerBackdrop.addEventListener('click', () => this.closeDrawer());
  }

  closeDrawer() {
    this.drawer.classList.remove('active');
    this.drawerBackdrop.classList.remove('active');
    document.body.style.overflow = '';
  }

  addToCart(variantId, card) {
    const form = card.querySelector(`.ai-add-to-cart-container-${this.aiGenId} form`);
    if (!form) {
      console.warn('No theme form found for this product');
      return;
    }

    const hiddenInput = form.querySelector('input[name="id"]');
    if (hiddenInput) hiddenInput.value = variantId;

    // Use theme's native add-to-cart submission
    form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
  }

  formatMoney(cents) {
    const amount = cents / 100;
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }

  preloadBuyButtons() {
    // Pre-load buy buttons for initial visible products
    const initialProducts = this.gridContainer.querySelectorAll(`.ai-product-card-${this.aiGenId}`);
    initialProducts.forEach(card => {
      const quickAddBtn = card.querySelector('[data-quick-add]');
      if (quickAddBtn) {
        const handle = quickAddBtn.dataset.productHandle;
        // Pre-load in background
        this.loadBuyButtonsForDrawer({ handle }).catch(() => {});
      }
    });
  }

setupInfiniteScroll() {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting && !this.isLoading && this.hasMore) {
        this.loadMore();
      }
    });
  }, {
    rootMargin: '400px'
  });

  observer.observe(this.loadingIndicator);
}

async loadMore() {
  if (this.isLoading || !this.hasMore) {
    return;
  }

  this.isLoading = true;
  this.loadingIndicator.style.display = 'block';

  try {
    this.currentPage++;
    const url = `/collections/${this.collection}?page=${this.currentPage}`;
    console.log(`Loading page ${this.currentPage}: ${url}`);
    
    const response = await fetch(url);
    const html = await response.text();
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const productElements = doc.querySelectorAll('.product-item, .product-card, [class*="product"]');
    
    console.log(`Found ${productElements.length} product elements on page ${this.currentPage}`);

    if (productElements.length === 0) {
      this.hasMore = false;
      this.loadingIndicator.style.display = 'none';
      return;
    }

    const productLinks = Array.from(productElements).map(el => {
      const link = el.querySelector('a[href*="/products/"]');
      return link ? link.getAttribute('href') : null;
    }).filter(Boolean);

    console.log(`Found ${productLinks.length} product links`);

    for (const productUrl of productLinks) {
      const handle = productUrl.split('/products/')[1].split('?')[0];
      await this.addProductToGrid(handle);
    }

    if (productLinks.length === 0) {
      this.hasMore = false;
    }

  } catch (error) {
    console.error('Error loading more products:', error);
    this.hasMore = false;
  } finally {
    this.isLoading = false;
    if (!this.hasMore) {
      this.loadingIndicator.style.display = 'none';
      console.log('No more products to load');
    } else {
      this.loadingIndicator.style.display = 'none';
    }
  }
}
  async addProductToGrid(handle) {
    card.dataset.tags = (product.tags || []).join(',');

    try {
      const response = await fetch(`/products/${handle}.js`);
      const product = await response.json();

      const card = document.createElement('div');
      card.className = `ai-product-card-${this.aiGenId}`;
      card.dataset.productId = product.id;

      const imageUrl = product.featured_image || '';
      const imageHtml = imageUrl 
        ? `<img src="${imageUrl}" alt="${product.title}" class="ai-product-card__image-${this.aiGenId}" loading="lazy" width="600" height="600">`
        : `<div class="ai-product-card__image-placeholder-${this.aiGenId}">Product Image</div>`;

      card.innerHTML = `
        <div class="ai-product-card__image-wrapper-${this.aiGenId}">
          ${imageHtml}
        </div>
        <div class="ai-product-card__info-${this.aiGenId}">
          <div class="ai-product-card__details-${this.aiGenId}">
            <h3 class="ai-product-card__title-${this.aiGenId}">
              <a href="/products/${product.handle}">${product.title}</a>
            </h3>
            <div class="ai-product-card__price-${this.aiGenId}">
              ${this.formatMoney(product.price)}
            </div>
          </div>
          <button
            class="ai-product-card__quick-add-${this.aiGenId}"
            data-quick-add
            data-product-handle="${product.handle}"
            aria-label="Quick add ${product.title}"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </button>
        </div>
        <div class="ai-product-card__overlay-${this.aiGenId}" data-overlay>
          <div class="ai-overlay__title-${this.aiGenId}">Select size</div>
          <div class="ai-overlay__sizes-${this.aiGenId}" data-sizes></div>
          <button class="ai-overlay__button-${this.aiGenId}" data-add-button>
            Select Size
          </button>
          <div class="ai-add-to-cart-container-${this.aiGenId} hide" data-product-handle="${product.handle}">
            <!-- Buy buttons will be loaded when needed -->
          </div>
        </div>
      `;

      this.gridContainer.appendChild(card);
      this.loadedCount++;

      // Pre-load buy buttons for this new product
      this.loadBuyButtonsForDrawer(product).catch(() => {});

    } catch (error) {
      console.error('Error adding product to grid:', error);
    }
  }
}

// Dynamic custom element registration
document.addEventListener('DOMContentLoaded', function() {
  // Find all product grid elements and register them
  const productGrids = document.querySelectorAll('[class*="ai-product-grid"]');
  
  productGrids.forEach(grid => {
    const classes = grid.className.split(' ');
    const aiGenIdClass = classes.find(cls => cls.startsWith('ai-product-grid-'));
    
    if (aiGenIdClass) {
      const aiGenId = aiGenIdClass.replace('ai-product-grid-', '');
      const elementName = `product-grid-${aiGenId}`;
      
      // Only register if not already registered
      if (!customElements.get(elementName)) {
        customElements.define(elementName, class extends ProductGrid {
          constructor() {
            super();
            this.aiGenId = aiGenId;
          }
        });
      }
    }
  });
});